import { downloadArtifact } from '@api/reportingAgent';
import { DataSheet, type DataSheetCaptureHandle, type DataSheetColumn } from '@components/ui/DataSheet';
import EntityFilterDropdown, {
    type FilterParam,
    type FilterValue,
} from '@components/ui/EntityFilterDropdown';
import type {
    ResultTablePayload,
    TableColumn,
    TableFilter,
} from '@diagon-alley/common';
import { ActionIcon, Card } from '@mantine/core';
import { DownloadSimple, EnvelopeSimple } from '@phosphor-icons/react';
import AppProviderContext from '@providers/App/context';
import { formatMoney, formatCount } from '@utils/currency';
import { lazy, Suspense, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

import RecipeRow from './RecipeRow';
import { savedReportRunPath, useReportTurn } from './reportTurn';
import styles from './ReportsContainer.module.css';

/**
 * Columns get a concrete width so DataSheet's fixed table layout can overflow
 * into its horizontal scroller. Text needs room to breathe; numbers and dates
 * are narrow and predictable.
 */
const EmailComposerDrawer = lazy(() => import('../reportSchedule/EmailComposerDrawer'));

const COLUMN_WIDTH_BY_FORMAT: Record<string, number> = {
    number: 120,
    currency: 130,
    percent: 100,
    date: 130,
    string: 180,
};

interface ResultTableCardProps {
    payload: ResultTablePayload;
}

/**
 * Renders an inline showResultTable payload through DataSheet, wrapped
 * in a custom card chrome with an in-card title strip and a metadata
 * strip ("20 rows · Generated 3:40 PM"). The DataSheet's built-in
 * header bar is suppressed in favor of this lighter treatment, so the
 * strip carries the download shortcut and the same Email composer every
 * DataSheet has: picture in the body, the table as an XLSX. Send now
 * works on any result; scheduling needs the report saved, because a
 * schedule reopens `/reports?run=<id>` headlessly, which re-runs the
 * saved recipe and renders this card again under the same sheet name.
 */
export default function ResultTableCard({ payload }: ResultTableCardProps) {
    const columns = useMemo(
        () => payload.columns.map(toDataSheetColumn),
        [payload.columns],
    );

    // Filter dropdowns the agent declared. Options are derived from the rows
    // themselves rather than shipped by the backend, so they can never offer a
    // value that isn't in the table.
    const [filterValue, setFilterValue] = useState<FilterValue>({});
    const filterConfigs = useMemo(
        () => buildFilterConfigs(payload.filters, payload.rows),
        [payload.filters, payload.rows],
    );

    const rows = useMemo(
        () => applyFilters(payload.rows, filterValue),
        [payload.rows, filterValue],
    );

    const downloadUrl = payload.downloadUrl;
    const filename = useMemo(
        () => extractFilename(downloadUrl) ?? 'report.csv',
        [downloadUrl],
    );

    // Capture the time when the payload first arrives so the strip
    // reads "Generated 3:40 PM" — fixed at render, not ticking.
    const [generatedAt] = useState(() => new Date());
    const generatedLabel = generatedAt.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
    });

    const totalRowCount = payload.rows.length;
    const rowCount = rows.length;
    const isFiltered = rowCount !== totalRowCount;
    const rowLabel =
        totalRowCount === 1
            ? '1 row'
            : `${formatCount(totalRowCount)} rows`;
    const metaParts = [rowLabel, `Generated ${generatedLabel}`];

    const { savedReport } = useReportTurn();
    // A saved report's table goes by the report's name: a title can carry the day it ran,
    // and a schedule finds the table by this name on every later run.
    const emailName = savedReport?.name ?? (payload.title?.trim() || 'Report');
    const captureRef = useRef<DataSheetCaptureHandle | null>(null);
    const [emailOpen, setEmailOpen] = useState(false);
    const currentUserEmail = useContext(AppProviderContext)?.currentUser?.email ?? '';
    const capture = <R,>(run: (handle: DataSheetCaptureHandle) => Promise<R>): Promise<R> =>
        captureRef.current ? run(captureRef.current) : Promise.reject(new Error('The table is not ready to capture'));

    const recipe = payload.meta?.recipe;
    const aggregateRow = useMemo(
        () => formatAggregateRow(payload.aggregateRow, payload.columns),
        [payload.aggregateRow, payload.columns],
    );

    return (
        <Card withBorder radius="md" className={styles.tableCard} p={0}>
            {(payload.title || downloadUrl || totalRowCount > 0) && (
                <div className={styles.titleStrip}>
                    <div className={styles.titleStripText}>
                        {payload.title && (
                            <div className={styles.titleStripTitle}>
                                {payload.title}
                            </div>
                        )}
                        <div className={styles.titleStripMeta}>
                            {metaParts.join(' · ')}
                        </div>
                    </div>
                    {downloadUrl && (
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={() => {
                                void downloadArtifact(downloadUrl, filename);
                            }}
                            title="Download CSV"
                            aria-label="Download CSV"
                        >
                            <DownloadSimple size={14} weight="bold" />
                        </ActionIcon>
                    )}
                    {totalRowCount > 0 && (
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={() => setEmailOpen(true)}
                            title="Email or schedule this table"
                            aria-label="Email this table"
                        >
                            <EnvelopeSimple size={14} weight="bold" />
                        </ActionIcon>
                    )}
                </div>
            )}
            {filterConfigs.length > 0 && (
                <div className={styles.filterStrip}>
                    {filterConfigs.map((config) => (
                        <EntityFilterDropdown
                            key={config.key}
                            label={config.label}
                            params={config.params}
                            value={filterValue}
                            onChange={setFilterValue}
                        />
                    ))}
                    {isFiltered && (
                        <span className={styles.filterStripCount}>
                            {formatCount(rowCount)} of{' '}
                            {formatCount(totalRowCount)} rows
                        </span>
                    )}
                </div>
            )}
            {totalRowCount === 0 ? (
                <div className={styles.tableEmpty}>
                    No results for this query.
                </div>
            ) : rowCount === 0 ? (
                <div className={styles.tableEmpty}>
                    No rows match the selected filters.
                </div>
            ) : (
                <DataSheet
                    columns={columns}
                    data={rows}
                    hideHeaderBar
                    enableDownload={false}
                    getRowKey={(_row, index) => `row-${index}`}
                    maxHeight={420}
                    aggregateRow={aggregateRow}
                    emailName={emailName}
                    captureRef={captureRef}
                />
            )}
            {emailOpen && (
                <Suspense fallback={null}>
                    <EmailComposerDrawer
                        opened
                        onClose={() => setEmailOpen(false)}
                        defaultName={emailName}
                        currentUserEmail={currentUserEmail}
                        currentFilters={{}}
                        captureImage={(maxVisualRows) => capture((handle) => handle.captureViewImage({ maxVisualRows }))}
                        captureData={(maxVisualRows) => capture((handle) => handle.captureViewData({ maxVisualRows }))}
                        pageCapture={savedReport ? { path: savedReportRunPath(savedReport), sheetKey: emailName } : undefined}
                        scheduleBlockedReason="Save the report to schedule it — every send re-runs the saved report. Send now works as is."
                    />
                </Suspense>
            )}
            {recipe && <RecipeRow recipe={recipe} />}
        </Card>
    );
}

function toDataSheetColumn(
    col: TableColumn,
): DataSheetColumn<Record<string, unknown>> {
    return {
        key: col.key,
        header: col.label,
        align: col.align,
        width: columnWidth(col),
        ...(col.group ? { group: col.group } : {}),
        render: (value) => formatCellValue(value, col.format),
    };
}

/**
 * Reports are shaped at runtime, so no one hand-tunes these. Start from the
 * format's natural width, then widen for a long header — a header that can't
 * fit wraps to several lines and drags every row down with it.
 */
function columnWidth(col: TableColumn): number {
    const base = COLUMN_WIDTH_BY_FORMAT[col.format ?? 'string'] ?? 180;
    const headerWidth = col.label.length * 8 + 32;
    return Math.min(Math.max(base, headerWidth), 280);
}

/**
 * Builds one dropdown per declared filter, with its options taken from the
 * distinct values actually present in the rows. Each dropdown owns a single
 * param keyed by the column key, which is what lets them share one FilterValue.
 */
function buildFilterConfigs(
    filters: TableFilter[] | undefined,
    rows: Array<Record<string, unknown>>,
): Array<{ key: string; label: string; params: FilterParam[] }> {
    if (!filters?.length) return [];

    return filters.flatMap((filter) => {
        const values = [
            ...new Set(
                rows
                    .map((row) => row[filter.key])
                    .filter((value) => value != null && value !== '')
                    .map(String),
            ),
        ].sort((a, b) => a.localeCompare(b));

        if (values.length === 0) return [];

        return [
            {
                key: filter.key,
                label: filter.label,
                params: [
                    {
                        key: filter.key,
                        label: filter.label,
                        mode: filter.mode ?? 'multi',
                        options: values.map((value) => ({
                            label: value,
                            value,
                        })),
                        searchPlaceholder: `Search ${filter.label.toLowerCase()}…`,
                    },
                ],
            },
        ];
    });
}

/** An empty selection on a param means "all", so it contributes no predicate. */
function applyFilters(
    rows: Array<Record<string, unknown>>,
    filterValue: FilterValue,
): Array<Record<string, unknown>> {
    const active = Object.entries(filterValue).filter(
        ([, selected]) => selected.length > 0,
    );
    if (active.length === 0) return rows;

    return rows.filter((row) =>
        active.every(([key, selected]) => selected.includes(String(row[key]))),
    );
}

function formatCellValue(
    value: unknown,
    format: TableColumn['format'],
): ReactNode {
    if (value == null || value === '') return '';

    switch (format) {
        case 'number':
            return typeof value === 'number'
                ? formatCount(value)
                : String(value);
        case 'currency':
            return typeof value === 'number'
                ? formatMoney(value)
                : String(value);
        case 'percent':
            return typeof value === 'number'
                ? `${value.toFixed(1)}%`
                : String(value);
        case 'date': {
            const parsed = new Date(String(value));
            return Number.isNaN(parsed.getTime())
                ? String(value)
                : parsed.toLocaleDateString();
        }
        case 'string':
        default:
            return String(value);
    }
}

/** The totals row the backend computed, formatted like the column it sits under. */
function formatAggregateRow(
    row: Record<string, unknown> | undefined,
    columns: TableColumn[],
): Partial<Record<string, ReactNode>> | undefined {
    if (!row) return undefined;
    const formatted: Partial<Record<string, ReactNode>> = {};
    for (const column of columns) {
        if (row[column.key] === undefined) continue;
        formatted[column.key] =
            typeof row[column.key] === 'string'
                ? String(row[column.key])
                : formatCellValue(row[column.key], column.format);
    }
    return formatted;
}

function extractFilename(url: string | undefined): string | null {
    if (!url) return null;
    const parts = url.split('/');
    return parts[parts.length - 1] || null;
}
